import React, { useState } from 'react';
import { PipelineProject } from '../../types/pipeline';
import StatusBadge from './StatusBadge';
import StagePill from './StagePill';
import ProgressBar from './ProgressBar';

interface PipelineTableProps {
  projects: PipelineProject[];
  onSelectProject: (project: PipelineProject) => void;
}

type SortKey = 'name' | 'target' | 'indication' | 'stage' | 'status' | 'lead' | 'compounds' | 'progress' | 'lastUpdate';

const PipelineTable: React.FC<PipelineTableProps> = ({ projects, onSelectProject }) => {
  const [sortKey, setSortKey] = useState<SortKey>('progress');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sorted = [...projects].sort((a, b) => {
    let cmp = 0;
    const av = a[sortKey as keyof PipelineProject];
    const bv = b[sortKey as keyof PipelineProject];
    if (typeof av === 'string' && typeof bv === 'string') cmp = av.localeCompare(bv);
    else if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <span className="text-gray-300 ml-0.5">↕</span>;
    return <span className="text-teal-500 ml-0.5">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const Th: React.FC<{ col: SortKey; children: React.ReactNode; className?: string }> = ({ col, children, className = '' }) => (
    <th
      className={`px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none ${className}`}
      onClick={() => handleSort(col)}
    >
      <span className="inline-flex items-center gap-0.5">
        {children}
        <SortIcon col={col} />
      </span>
    </th>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse min-w-[1000px]">
        <thead>
          <tr className="border-b border-gray-200">
            <Th col="name" className="text-left">Program</Th>
            <Th col="target" className="text-left">Target</Th>
            <Th col="indication" className="text-left">Indication</Th>
            <Th col="stage" className="text-left">Stage</Th>
            <Th col="status" className="text-left">Status</Th>
            <Th col="lead" className="text-left">Lead</Th>
            <Th col="compounds" className="text-right">Compounds</Th>
            <Th col="progress" className="text-left">Progress</Th>
            <Th col="lastUpdate" className="text-right">Updated</Th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((p, i) => (
            <tr
              key={p.id}
              className="border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => onSelectProject(p)}
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <td className="px-3 py-3">
                <span className="font-medium text-gray-900 text-sm">{p.name}</span>
              </td>
              <td className="px-3 py-3 text-sm text-gray-600">{p.target}</td>
              <td className="px-3 py-3 text-sm text-gray-600">{p.indication}</td>
              <td className="px-3 py-3"><StagePill stageId={p.stage} /></td>
              <td className="px-3 py-3"><StatusBadge status={p.status} /></td>
              <td className="px-3 py-3 text-sm text-gray-600">{p.lead}</td>
              <td className="px-3 py-3 text-sm text-gray-700 text-right font-medium">{p.compounds.toLocaleString()}</td>
              <td className="px-3 py-3">
                <ProgressBar value={p.progress} height="h-1.5" showLabel className="w-24" />
              </td>
              <td className="px-3 py-3 text-sm text-gray-400 text-right whitespace-nowrap">
                {p.lastUpdate ? new Date(p.lastUpdate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PipelineTable;
